/**
 * DecisionButtons — przyciski decyzji osoby akceptującej etap (model WF: decyzja = wybór DROGI
 * narysowanej w definicji procesu).
 *
 * Układ: „Akceptacja" (wyróżniona, `primary`) + do trzech dróg jako przyciski (`secondary`, jeden
 * styl, bez kolorów dróg) + menu „…" z pozostałymi drogami. Droga domyślna jest pierwsza, z gwiazdką
 * ★ i dymkiem wyjaśniającym, że to droga domyślna. Kolejność dróg liczy serwer (domyślna → ustawiona
 * przez admina → id); tu tylko pilnujemy, żeby domyślna była pierwsza.
 *
 * Komponent NIC nie wysyła — każdy przycisk wywołuje `onOpen`:
 *   - „Akceptacja"            → `onOpen(null)`   (okno z preselekcją drogi domyślnej),
 *   - przycisk / pozycja menu → `onOpen(routeKey)` (okno z tą drogą wybraną).
 * Okno „Akceptacja" (`AcceptanceDialog`) otwiera wołający. Komponent jest reużywalny (baner na karcie
 * rekordu, wiersz listy decyzji): rozmiar przycisków przez `size`.
 *
 * Menu „…" zawiera także (opcjonalnie) pozycję „Cofnij do poprzedniego etapu" na końcu, oddzieloną
 * kreską od dróg: `stepBackAvailable` + `onOpenStepBack`. Menu pokazuje się wtedy nawet przy 1–3
 * drogach (gdy nic nie wylądowało w nadmiarze, pozycja jest w nim jedyna). Pozycja to NIE droga —
 * woła `onOpenStepBack()` (wołający otwiera okno „Akceptacja" z wybranym cofnięciem), a fokus
 * zachowuje jak wybór drogi z menu. Przy `primaryOnly` pozycji nie ma.
 *
 * Blokada na czas zapisu (formularz w edycji, patrz `useSaveBeforeDecision`): `disabled` blokuje
 * wszystkie przyciski naraz, a `loadingTarget` wskazuje ten, który pokazuje kręciołek (kliknięty;
 * gdy kliknięta droga siedzi w menu — przycisk „…"; cofnięcie, które zawsze siedzi w menu, wskazuje
 * `{ kind: 'stepBack' }` — kręciołek na „…", „Akceptacja" bez kręciołka; „…" stoi wtedy nawet
 * wtedy, gdy opcja cofnięcia zniknęła w trakcie zapisu). Oba propsy są opcjonalne: wiersz listy
 * decyzji ich nie podaje i zachowuje się jak dotąd. Etykieta przycisku „…" (dymek i `aria-label`):
 * „Więcej akcji" przy menu z cofnięciem, „Pozostałe drogi" przy samych drogach.
 *
 * Klawiatura: przyciski to prymityw `Button`, menu „…" to Popover (Radix) z pozycjami-przyciskami —
 * Tab / Enter / Spacja / Esc działają bez własnej obsługi.
 *
 * Dostępność: w liście wiele wierszy ma identyczne przyciski („Akceptacja", te same drogi), więc
 * wołający może podać `groupLabel` (np. „Decyzja w sprawie nr 42") — pasek dostaje wtedy
 * `role="group"` z tą etykietą i czytnik ekranu odróżnia wiersze. Bez `groupLabel` (baner na
 * karcie rekordu — jeden pasek na ekranie) znacznik grupy się nie pojawia.
 *
 * Układ dla wiersza listy (`layout="list"`): przyciski w JEDNEJ linii (bez zawijania — w wierszu
 * listy kolumna „Decyzja" bywa przypięta, a wiersz ma zostać niski), nazwa drogi ucinana po 10rem
 * zamiast 14rem, a dymek z PEŁNĄ nazwą drogi pokazuje się tylko wtedy, gdy nazwa jest faktycznie
 * ucięta (`scrollWidth > clientWidth` elementu z nazwą, mierzone przy montażu, zmianie nazwy i
 * zmianie rozmiaru przez `ResizeObserver`). Droga domyślna z uciętą nazwą ma JEDEN dymek: pełna
 * nazwa + podpowiedź „droga domyślna" (dwie linie, czytnik ekranu dostaje samą podpowiedź); z
 * nieuciętą — sama podpowiedź jak dotąd. Drzewo przycisku drogi jest w tym układzie stałe
 * (przycisk zawsze owinięty w dymek, ucięcie włącza go lub wyłącza), więc zmiana rozmiaru nie
 * przemontowuje przycisku. Bez `layout` (baner na karcie rekordu) komponent działa dokładnie jak
 * dotąd.
 *
 * `primaryOnly` (element formularza „Przycisk akceptacji"): sama „Akceptacja", bez przycisków dróg i
 * bez menu „…"; kręciołek i blokada na czas zapisu działają jak zwykle.
 */

import type { ReactElement } from 'react';
import { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import type { AvailableRoute } from '@saas/shared';
import { MoreHorizontal } from 'lucide-react';

import { Button, type ButtonSize } from '@/components/Button';
import { Tooltip } from '@/components/Tooltip';
import { routeDisplayLabel } from '@/lib/processes/route-display';
import { cn } from '@/lib/utils';

/** Ile dróg pokazujemy jako osobne przyciski; reszta ląduje w menu „Pozostałe drogi". */
export const MAX_INLINE_ROUTES = 3;

/**
 * Układ paska przycisków: `default` (baner na karcie rekordu — przyciski zawijają się, nazwa drogi
 * do 14rem) albo `list` (wiersz listy decyzji — jedna linia, nazwa drogi do 10rem z dymkiem przy
 * uciętej nazwie).
 */
export type DecisionButtonsLayout = 'default' | 'list';

/** Maksymalna szerokość przycisku drogi per układ — dłuższa nazwa jest ucinana wielokropkiem. */
const ROUTE_BUTTON_MAX_WIDTH_CLASS: Record<DecisionButtonsLayout, string> = {
  default: 'max-w-[14rem]',
  list: 'max-w-[10rem]',
};

/**
 * Cel otwarcia okna decyzji / kręciołka: droga albo cofnięcie. `routeKey: null` = „Akceptacja"
 * (droga domyślna); `kind: 'stepBack'` = pozycja „Cofnij do poprzedniego etapu" z menu „…".
 */
export type DecisionTarget =
  | { readonly kind: 'route'; readonly routeKey: string | null }
  | { readonly kind: 'stepBack' };

export interface DecisionButtonsProps {
  /** Drogi dostępne na etapie (`InstanceStep.availableRoutes`, liczone przez serwer). */
  routes: readonly AvailableRoute[];
  /** `null` = przycisk „Akceptacja" (droga domyślna); `string` = klucz drogi z przycisku/menu. */
  onOpen: (routeKey: string | null) => void;
  disabled?: boolean;
  /**
   * Który przycisk pokazuje kręciołek (czeka na wynik zapisu) — `DecisionTarget`:
   * `{ kind: 'route', routeKey: null }` = „Akceptacja", `{ kind: 'route', routeKey }` = przycisk
   * tej drogi albo, gdy droga siedzi w menu, przycisk „…"; `{ kind: 'stepBack' }` = przycisk „…"
   * (cofnięcie siedzi w menu). Kręciołek sam blokuje swój przycisk; pozostałe blokuje `disabled`
   * (wołający ustawia oba razem). Brak / `null` = żaden przycisk nie pokazuje kręciołka.
   */
  loadingTarget?: DecisionTarget | null;
  /**
   * Czy w menu „…” ma stać pozycja „Cofnij do poprzedniego etapu” (wiersz/baner wie to z serwera).
   * Pozycja działa tylko razem z `onOpenStepBack`; przy `primaryOnly` nigdy się nie pokazuje.
   * Domyślnie `false`.
   */
  stepBackAvailable?: boolean;
  /** Klik pozycji „Cofnij do poprzedniego etapu” — wołający otwiera okno z wybranym cofnięciem. */
  onOpenStepBack?: () => void;
  /**
   * Rozmiar przycisków (`sm` w banerze i w wierszu listy); układ banera lub listy wybiera `layout`.
   */
  size?: ButtonSize;
  /**
   * Etykieta paska przycisków dla czytnika ekranu (`role="group"` + `aria-label`) — podawana tam,
   * gdzie takie paski się powtarzają (wiersze listy), np. „Decyzja w sprawie nr 42".
   */
  groupLabel?: string;
  /**
   * Układ paska: `default` (baner na karcie rekordu) albo `list` (wiersz listy decyzji — bez
   * zawijania, przycisk drogi ucinany po 10rem zamiast 14rem, dymek z pełną nazwą drogi tylko przy
   * uciętej nazwie). Bez propa komponent działa dokładnie jak dotąd.
   */
  layout?: DecisionButtonsLayout;
  /**
   * Tylko przycisk „Akceptacja” — bez przycisków dróg i bez menu „…” (element formularza „Przycisk
   * akceptacji”: okno decyzji samo pozwala wybrać drogę). Domyślnie `false` = pełny pasek jak dotąd.
   */
  primaryOnly?: boolean;
  testIdPrefix?: string;
}

/**
 * Czy tekst w elemencie jest ucięty (`scrollWidth > clientWidth`). Mierzy przy montażu elementu,
 * zmianie tekstu i zmianie rozmiaru elementu (`ResizeObserver`); `null` = nie mierz (zawsze `false`).
 * Element podajemy jako stan (ref-callback), nie `useRef`: ustawia się dopiero po pierwszym
 * renderze (albo po przemontowaniu rodzica), a efekt musi wtedy złapać TEN element.
 */
function useIsTruncated(element: HTMLElement | null, text: string): boolean {
  const [truncated, setTruncated] = useState(false);
  useLayoutEffect(() => {
    if (!element) return undefined;
    const measure = (): void => setTruncated(element.scrollWidth > element.clientWidth);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
    // `text` w zależnościach: ta sama szerokość elementu, ale inna nazwa → inny wynik pomiaru.
  }, [element, text]);
  return element !== null && truncated;
}

interface InlineRouteButtonProps {
  route: AvailableRoute;
  /** Nazwa drogi do pokazania (po `routeDisplayLabel`). */
  label: string;
  size: ButtonSize;
  layout: DecisionButtonsLayout;
  disabled: boolean;
  loading: boolean;
  onOpen: (routeKey: string) => void;
  testId: string;
  /** Podpowiedź przy drodze domyślnej („Droga domyślna — wybierze ją przycisk «Akceptacja»"). */
  defaultRouteHint: string;
}

/**
 * Przycisk jednej drogi w pasku.
 *
 * Układ `default` (baner): droga domyślna ma dymek z podpowiedzią, pozostałe bez dymka — drzewo jak
 * dotąd.
 *
 * Układ `list`: przycisk drogi ma STAŁY kształt drzewa niezależnie od tego, czy nazwa jest ucięta —
 * ZAWSZE owinięty w `Tooltip`. Wynik pomiaru ucięcia zmienia tylko treść / stan dymka, nigdy
 * drzewo, więc zmiana rozmiaru kolumny albo `loading` nie przemontowuje przycisku (nie gubi fokusu
 * ani kręciołka). Zwykła droga: dymek z pełną nazwą, wyłączony (`disabled`), gdy nazwa się mieści.
 * Droga domyślna: dymek jest aktywny zawsze — przy uciętej nazwie ma dwie linie (nazwa +
 * podpowiedź), przy nieuciętej samą podpowiedź.
 *
 * Opis dla czytnika ekranu (`aria-describedby` triggera): droga domyślna z uciętą nazwą dostaje
 * `ariaLabel` = sama podpowiedź, bo pełną nazwę czytnik ma już z nazwy przycisku. Zwykła droga
 * dostaje w opisie tę samą nazwę, którą ma z nazwy przycisku — dubel jest zamierzony: Radix nie
 * przyjmuje pustego opisu (pusty `ariaLabel` = brak etykiety, wraca treść dymka), a nazwa przycisku
 * jest zawsze pełna (ucięcie dotyczy wyłącznie wyglądu). Dubel dotyczy tylko uciętej nazwy — przy
 * nieuciętej dymek jest wyłączony.
 */
function InlineRouteButton({
  route,
  label,
  size,
  layout,
  disabled,
  loading,
  onOpen,
  testId,
  defaultRouteHint,
}: InlineRouteButtonProps): ReactElement {
  const isList = layout === 'list';
  const [labelElement, setLabelElement] = useState<HTMLSpanElement | null>(null);
  const truncated = useIsTruncated(isList ? labelElement : null, label);

  const button = (
    <Button
      variant="secondary"
      size={size}
      className={ROUTE_BUTTON_MAX_WIDTH_CLASS[layout]}
      data-testid={testId}
      disabled={disabled}
      loading={loading}
      onClick={() => onOpen(route.routeKey)}
    >
      {route.isDefault ? <span aria-hidden="true">★</span> : null}
      <span ref={isList ? setLabelElement : undefined} className="truncate">
        {label}
      </span>
    </Button>
  );

  if (isList) {
    if (route.isDefault) {
      // Jeden dymek, zawsze aktywny: przy uciętej nazwie pełna nazwa w pierwszej linii, podpowiedź
      // w drugiej (`Tooltip` zachowuje łamanie linii); czytnik dostaje samą podpowiedź.
      return (
        <Tooltip
          content={truncated ? `${label}\n${defaultRouteHint}` : defaultRouteHint}
          ariaLabel={truncated ? defaultRouteHint : undefined}
        >
          {button}
        </Tooltip>
      );
    }
    return (
      <Tooltip content={label} disabled={!truncated}>
        {button}
      </Tooltip>
    );
  }

  return route.isDefault ? <Tooltip content={defaultRouteHint}>{button}</Tooltip> : button;
}

export function DecisionButtons({
  routes,
  onOpen,
  disabled = false,
  loadingTarget = null,
  size = 'sm',
  groupLabel,
  layout = 'default',
  primaryOnly = false,
  stepBackAvailable = false,
  onOpenStepBack,
  testIdPrefix = 'decision-buttons',
}: DecisionButtonsProps): ReactElement {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const moreTriggerRef = useRef<HTMLButtonElement>(null);
  // Menu zamknięte wyborem drogi: po nim fokus ma przejąć okno „Akceptacja", więc Popover nie
  // może po swojemu odesłać fokusu na przycisk „…" (zrobiłby to już po otwarciu okna).
  const closedBySelectionRef = useRef(false);

  // Stabilne sortowanie: droga domyślna pierwsza, reszta w kolejności z serwera.
  const ordered = [...routes].sort((a, b) => Number(b.isDefault) - Number(a.isDefault));
  // `primaryOnly`: żadnych dróg w pasku ani w menu — zostaje sama „Akceptacja”.
  const inlineRoutes = primaryOnly ? [] : ordered.slice(0, MAX_INLINE_ROUTES);
  const overflowRoutes = primaryOnly ? [] : ordered.slice(MAX_INLINE_ROUTES);
  const unnamed = t('tenant.processes.actions.routeUnnamed');
  // Kręciołek: „Akceptacja" (`routeKey: null`), przycisk drogi albo — dla drogi z menu i dla
  // cofnięcia — przycisk „…".
  const loadingAccept = loadingTarget?.kind === 'route' && loadingTarget.routeKey === null;
  const loadingRouteKey = loadingTarget?.kind === 'route' ? loadingTarget.routeKey : null;
  const loadingStepBack = loadingTarget?.kind === 'stepBack';
  // Pozycja „Cofnij…” w menu: tylko z obsługą kliknięcia i nigdy przy `primaryOnly`.
  const showStepBack = !primaryOnly && stepBackAvailable && onOpenStepBack !== undefined;
  const loadingMore =
    loadingStepBack ||
    (loadingRouteKey !== null &&
      overflowRoutes.some((route) => route.routeKey === loadingRouteKey));
  // Przycisk „…” stoi też wtedy, gdy kręciołek dotyczy cofnięcia, a opcja zniknęła w trakcie zapisu
  // (sam kręciołek bez pozycji w menu — przycisk i tak jest zablokowany do końca zapisu).
  const showMore = overflowRoutes.length > 0 || showStepBack || (!primaryOnly && loadingStepBack);
  // Menu z pozycją cofnięcia (albo z kręciołkiem cofnięcia) to „Więcej akcji”; same drogi — „Pozostałe drogi”.
  const moreLabel =
    showStepBack || loadingStepBack
      ? t('tenant.processes.acceptance.moreActions')
      : t('tenant.processes.acceptance.moreRoutes');

  /** Wspólny sposób zamknięcia menu wyborem pozycji (fokus zostaje na „…” dla okna). */
  const closeMenuBySelection = (): void => {
    // Fokus na przycisk „…" PRZED otwarciem okna: okno zapamięta go i odda po zamknięciu
    // (pozycja menu, która go miała, znika razem z menu).
    moreTriggerRef.current?.focus();
    closedBySelectionRef.current = true;
    setMenuOpen(false);
  };
  const selectFromMenu = (routeKey: string): void => {
    closeMenuBySelection();
    onOpen(routeKey);
  };
  const selectStepBackFromMenu = (): void => {
    closeMenuBySelection();
    onOpenStepBack?.();
  };

  const isList = layout === 'list';
  const defaultRouteHint = t('tenant.processes.acceptance.defaultRouteHint');

  return (
    <div
      // Układ listy: jedna linia — w wierszu listy kolumna bywa przypięta, a wiersz ma zostać niski.
      className={cn('flex items-center gap-2', isList ? 'flex-nowrap' : 'flex-wrap')}
      role={groupLabel ? 'group' : undefined}
      aria-label={groupLabel || undefined}
      data-testid={`${testIdPrefix}-root`}
    >
      <Button
        size={size}
        // W jednej linii „Akceptacja" nie może się ściskać przy ciasnym kontenerze.
        className={isList ? 'shrink-0' : undefined}
        data-testid={`${testIdPrefix}-accept`}
        disabled={disabled}
        loading={loadingAccept}
        onClick={() => onOpen(null)}
      >
        {t('tenant.processes.actions.approve')}
      </Button>

      {inlineRoutes.map((route) => (
        <InlineRouteButton
          key={route.routeKey}
          route={route}
          label={routeDisplayLabel(route, unnamed)}
          size={size}
          layout={layout}
          disabled={disabled}
          loading={route.routeKey === loadingRouteKey}
          onOpen={onOpen}
          testId={`${testIdPrefix}-route-${route.routeKey}`}
          defaultRouteHint={defaultRouteHint}
        />
      ))}

      {showMore ? (
        <PopoverPrimitive.Root open={menuOpen} onOpenChange={setMenuOpen}>
          <Tooltip content={moreLabel}>
            <PopoverPrimitive.Trigger asChild>
              <Button
                ref={moreTriggerRef}
                variant="secondary"
                size={size}
                className={cn(
                  'px-0',
                  size === 'sm' ? 'w-8' : size === 'md' ? 'w-10' : 'w-11',
                  isList && 'shrink-0',
                )}
                aria-label={moreLabel}
                data-testid={`${testIdPrefix}-more`}
                disabled={disabled}
                loading={loadingMore}
              >
                {/* Kręciołek (z `loading`) zastępuje ikonę „…". */}
                {loadingMore ? null : <MoreHorizontal size={16} aria-hidden="true" />}
              </Button>
            </PopoverPrimitive.Trigger>
          </Tooltip>
          <PopoverPrimitive.Portal>
            <PopoverPrimitive.Content
              align="end"
              sideOffset={4}
              aria-label={moreLabel}
              data-testid={`${testIdPrefix}-more-menu`}
              className="z-[10001] flex min-w-[12rem] max-w-[20rem] flex-col gap-0.5 rounded-lg border border-border-primary bg-bg-elevated p-1 shadow-lg focus:outline-none"
              onCloseAutoFocus={(event) => {
                if (closedBySelectionRef.current) {
                  event.preventDefault();
                  closedBySelectionRef.current = false;
                }
              }}
            >
              {overflowRoutes.map((route) => (
                <Button
                  key={route.routeKey}
                  variant="ghost"
                  size={size}
                  className="w-full justify-start font-normal"
                  data-testid={`${testIdPrefix}-more-${route.routeKey}`}
                  onClick={() => selectFromMenu(route.routeKey)}
                >
                  {route.isDefault ? <span aria-hidden="true">★</span> : null}
                  <span className="truncate">{routeDisplayLabel(route, unnamed)}</span>
                </Button>
              ))}
              {showStepBack ? (
                <>
                  {/* Cofnięcie to osobna akcja, nie droga — przy drogach w menu oddzielona kreską. */}
                  {overflowRoutes.length > 0 ? (
                    <div
                      role="separator"
                      className="my-0.5 border-t border-border-primary"
                      data-testid={`${testIdPrefix}-more-separator`}
                    />
                  ) : null}
                  <Button
                    variant="ghost"
                    size={size}
                    className="w-full justify-start font-normal"
                    data-testid={`${testIdPrefix}-more-step-back`}
                    onClick={selectStepBackFromMenu}
                  >
                    <span className="truncate">
                      {t('tenant.processes.acceptance.stepBack.label')}
                    </span>
                  </Button>
                </>
              ) : null}
            </PopoverPrimitive.Content>
          </PopoverPrimitive.Portal>
        </PopoverPrimitive.Root>
      ) : null}
    </div>
  );
}
